/**
 * usePreviewOptions Hook
 *
 * Manages preview effect toggle state (blur, gray, flip) for the moderation
 * work queue. Initializes from persisted user preferences and saves changes
 * immediately to localStorage via the userPreferencesStorage service.
 *
 * @module usePreviewOptions
 */

import { loadPreferences, setPreviewOptions as persistPreviewOptions } from '@core/services/userPreferencesStorage';
import { useCallback, useState } from 'react';

import type { PreviewOptions } from '../domain/PreviewOptions';
import { DEFAULT_PREVIEW_OPTIONS } from '../domain/PreviewOptions';

/** Return type for the usePreviewOptions hook */
export interface UsePreviewOptionsReturn {
  /** Current preview effect toggle states */
  previewOptions: PreviewOptions;
  /** Toggle a single preview effect by key */
  togglePreview: (key: keyof PreviewOptions) => void;
}

/**
 * Hook that manages preview effect toggle state with localStorage persistence.
 *
 * Initializes from stored user preferences (lazy useState initializer).
 * Each toggle immediately persists the new state to localStorage.
 */
export function usePreviewOptions(): UsePreviewOptionsReturn {
  const [previewOptions, setPreviewOptions] = useState<PreviewOptions>(() => {
    const stored = loadPreferences();

    return stored.previewOptions ?? DEFAULT_PREVIEW_OPTIONS;
  });

  const togglePreview = useCallback((key: keyof PreviewOptions) => {
    setPreviewOptions(prev => {
      const next = { ...prev, [key]: !prev[key] };

      persistPreviewOptions(next);

      return next;
    });
  }, []);

  return { previewOptions, togglePreview };
}
